import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faSearch,
  faBox,
  faMapMarkerAlt,
  faTruck,
  faCheckCircle,
  faTimesCircle,
  faClock,
  faCalendarAlt,
  faUser,
  faPhone,
  faEnvelope,
  faCreditCard,
  faChevronDown,
  faChevronUp,
  faWarehouse,
  faTruckFast,
  faHome
} from '@fortawesome/free-solid-svg-icons';
import { shipmentService } from '../../../../services/shipmentService';
import { getCustomerTrackingStatuses, formatTrackingCode } from '../../../../utils/formatters';

const inferTypeFromCode = (code) => {
  const c = String(code || '').toUpperCase();
  const idx = c.startsWith('FX') ? 2 : 0;
  const lead = c[idx];
  if (lead === '1') return 'local';
  if (lead === '2') return 'interstate';
  if (lead === '3') return 'international';
  return 'interstate';
};

const CountdownTimer = ({ targetDate }) => {
  const [timeLeft, setTimeLeft] = useState('');

  useEffect(() => {
    const update = () => {
      const now = new Date().getTime();
      const distance = targetDate - now;

      if (distance < 0) {
        setTimeLeft('Arriving soon');
      } else {
        const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((distance % (1000 * 60)) / 1000);
        setTimeLeft(`${hours}h ${minutes}m ${seconds}s`);
      }
    };
    
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [targetDate]);

  return <span className="text-red-600 font-bold">{timeLeft}</span>;
};

import { paymentsService } from '../../../../services/paymentsService';
import { configService } from '../../../../services/configService';

const Tracking = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [trackingNumber, setTrackingNumber] = useState(id || '');
  const [trackingData, setTrackingData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fullDetails, setFullDetails] = useState(null);
  const [showSenderDetails, setShowSenderDetails] = useState(false);
  const [showReceiverDetails, setShowReceiverDetails] = useState(false);
  const [showPackageDetails, setShowPackageDetails] = useState(false);
  const [showPaymentDetails, setShowPaymentDetails] = useState(false);
  const [animateProgress, setAnimateProgress] = useState(false);
  const [refreshStatus, setRefreshStatus] = useState('idle');
  const [statusConfig, setStatusConfig] = useState({
    origin: 'Your shipment has been registered and payment confirmed. Waiting for courier pickup from sender.',
    inTransit: 'Your package is on its way to the destination city. It\'s being transported through our delivery network.',
    outForDelivery: 'Your package is with the delivery courier and will be delivered today.',
    delivered: 'Your package has been successfully delivered to the recipient.'
  });
  const [bankDetails, setBankDetails] = useState(null);
  const [paymentMethodChoice, setPaymentMethodChoice] = useState('');
  const [receiptPreview, setReceiptPreview] = useState('');
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [settingPaymentMethod, setSettingPaymentMethod] = useState(false);

  useEffect(() => {
    // Fetch dynamic status messages
    configService.getStatusConfig().then(resp => {
      if (resp?.config) {
        setStatusConfig(prev => ({
          ...prev,
          origin: resp.config.origin || prev.origin,
          inTransit: resp.config.inTransit || prev.inTransit,
          outForDelivery: resp.config.outForDelivery || prev.outForDelivery,
          delivered: resp.config.delivered || prev.delivered
        }));
      }
    }).catch(err => console.error('Failed to load status messages', err));
  }, []);

  useEffect(() => {
    configService.getBankDetails().then(resp => {
      if (resp?.bank) setBankDetails(resp.bank);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (id) {
      setTrackingNumber(id);
      handleTrackShipment();
    }
  }, [id]);

  const handleTrackShipment = async () => {
    if (!trackingNumber.trim()) {
      setError('Please enter a tracking number');
      return;
    }

    setLoading(true);
    setError('');
    setTrackingData(null);
    setAnimateProgress(false);

    try {
      const resp = await shipmentService.track(trackingNumber.trim());
      const s = resp.shipment || {};
      const timeline = Array.isArray(s.statusHistory) ? s.statusHistory.map((h) => {
        let loc = h.note || h.status || 'Processing';
        const agentInfo = [h.updatedByName, h.updatedByEmail].filter(Boolean).join(' • ');
        if (agentInfo) {
          loc = loc ? `${loc} — ${agentInfo}` : agentInfo;
        }

        return {
          date: h.updatedAt ? new Date(h.updatedAt).toISOString().split('T')[0] : '',
          time: h.updatedAt ? new Date(h.updatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '',
          ms: h.updatedAt ? new Date(h.updatedAt).getTime() : null,
          location: loc,
          status: h.status || 'Update',
          updatedByName: h.updatedByName || '',
          updatedByEmail: h.updatedByEmail || '',
          updatedBy: h.updatedBy || '',
          completed: true
        };
      }) : [];

      setTrackingData({
        id: formatTrackingCode(s.trackingCode || trackingNumber),
        status: s.status || 'Pending',
        estimatedDelivery: '—',
        origin: s.pickupLocation || '—',
        destination: s.receiverName ? `${s.receiverName}` : '—',
        serviceType: s.serviceType || 'interstate',
        sender: {
          name: s.senderName || '—',
          phone: s.senderNumber || '—',
          email: s.email || '—',
          address: s.senderAddress || '—'
        },
        receiver: {
          name: s.receiverName || '—',
          phone: s.receiverNumber || '—',
          email: '—',
          address: s.receiverAddress || '—'
        },
        package: {
          weight: s.weight ? `${s.weight} kg` : '—',
          dimensions: '—',
          description: s.packageType || '—',
          items: s.numberOfItems || '—'
        },
        timeline
      });

      // Trigger animation after data loads
      setTimeout(() => setAnimateProgress(true), 100);

      try {
        const token = localStorage.getItem('token');
        if (token) {
          const my = await shipmentService.getMy(token);
          const list = my?.shipments || my || [];
          const match = list.find(x => (String(x.trackingCode || '').toUpperCase() === String(s.trackingCode || trackingNumber).toUpperCase()));
          if (match) {
            setFullDetails({
              shipmentId: match._id || match.id || null,
              trackingCode: formatTrackingCode(match.trackingCode || (s.trackingCode || trackingNumber)),
              email: match.email || '',
              price: typeof match.price === 'number' ? match.price : Number(match.price) || null,
              originalPrice: match.originalPrice,
              customPrice: match.customPrice,
              paymentType: match.paymentType || 'payNow',
              paymentStatus: match.paymentStatus || 'Pending',
              paymentMethod: match.paymentMethod || '',
              paymentReferenceCode: match.paymentReferenceCode || '',
              paymentReferenceSavedAt: match.paymentReferenceSavedAt || null,
              paymentReceiptData: match.paymentReceiptData || '',
              paymentReceiptUploadedAt: match.paymentReceiptUploadedAt || null,
              paymentConfirmedAt: match.paymentConfirmedAt || null
            });
          }
        }
      } catch (e) {
        setFullDetails(null);
      }
    } catch (err) {
      console.error('Tracking failed', err);
      setError(err?.response?.data?.msg || 'Shipment not found. Please check your tracking number.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusInfo = (status) => {
    const list = getCustomerTrackingStatuses(trackingData?.serviceType || inferTypeFromCode(trackingNumber));
    const idx = Math.max(0, list.indexOf(status));
    const total = Math.max(1, list.length - 1);
    const pct = Math.round((idx / total) * 100);
    if (status === 'Delivered') {
      return { color: 'green', icon: faCheckCircle, bg: 'bg-green-100', text: 'text-green-700', progress: 100 };
    }
    if (status === 'Out for Delivery') {
      return { color: 'purple', icon: faShippingFast, bg: 'bg-purple-100', text: 'text-purple-700', progress: pct };
    }
    if (status === 'In Transit' || status === 'International Transit' || status === 'In Transit to Airport') {
      return { color: 'yellow', icon: faTruck, bg: 'bg-yellow-100', text: 'text-yellow-700', progress: pct };
    }
    if (status === 'Origin' || status === 'Pickup' || status === 'Shipment by Carrier' || status === 'At Departure Airport' || status === 'At Destination Airport' || status === 'Custom Clearance') {
      return { color: 'blue', icon: faBox, bg: 'bg-blue-100', text: 'text-blue-700', progress: pct };
    }
    return { color: 'gray', icon: faClock, bg: 'bg-gray-100', text: 'text-gray-700', progress: pct };
  };

  const handlePayOnline = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert('You must be logged in to make a payment.');
        return;
      }
      const email = fullDetails?.email;
      const amount = fullDetails?.price;
      const sid = fullDetails?.shipmentId;
      if (!email || !amount || !sid) {
        alert('Missing payment details. Please contact support.');
        return;
      }
      const init = await paymentsService.initialize({ email, amount, shipmentId: sid }, token);
      const authUrl = init?.data?.authorization_url;
      if (authUrl) {
        window.location.href = authUrl;
      } else {
        throw new Error('Unable to start online payment. Please try again.');
      }
    } catch (err) {
      alert(err?.response?.data?.msg || err?.message || 'Failed to initialize payment');
    }
  };

  useEffect(() => {
    if (!fullDetails) return;
    const m = fullDetails.paymentMethod
      ? String(fullDetails.paymentMethod)
      : (fullDetails.paymentType === 'cashOnDelivery' ? 'cash' : 'bank_transfer');
    setPaymentMethodChoice(m);
    if (fullDetails.paymentReceiptData) {
      setReceiptPreview(fullDetails.paymentReceiptData);
    }
  }, [fullDetails]);

  const isPaymentConfirmed = (status) => {
    const s = String(status || '').toLowerCase();
    return s === 'paid' || s === 'confirmed' || s === 'payment confirmed';
  };

  const handleSetPaymentMethod = async (method) => {
    if (!fullDetails?.shipmentId) return;
    try {
      setSettingPaymentMethod(true);
      const token = localStorage.getItem('token');
      const res = await shipmentService.setPaymentMethod(fullDetails.shipmentId, method, token);
      const updated = res?.shipment;
      if (updated) {
        setFullDetails(prev => prev ? ({
          ...prev,
          paymentMethod: updated.paymentMethod || prev.paymentMethod,
          paymentStatus: updated.paymentStatus || prev.paymentStatus,
          paymentReceiptData: updated.paymentReceiptData || prev.paymentReceiptData,
          paymentReceiptUploadedAt: updated.paymentReceiptUploadedAt || prev.paymentReceiptUploadedAt,
          paymentReferenceCode: updated.paymentReferenceCode || prev.paymentReferenceCode,
          paymentReferenceSavedAt: updated.paymentReferenceSavedAt || prev.paymentReferenceSavedAt,
          paymentConfirmedAt: updated.paymentConfirmedAt || prev.paymentConfirmedAt
        }) : prev);
      }
      setPaymentMethodChoice(method);
    } catch (err) {
      alert(err?.response?.data?.msg || err?.message || 'Failed to set payment method');
    } finally {
      setSettingPaymentMethod(false);
    }
  };

  const handleReceiptFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === 'string') setReceiptPreview(result);
    };
    reader.readAsDataURL(file);
  };

  const handleUploadReceipt = async () => {
    if (!fullDetails?.shipmentId) return;
    if (!receiptPreview) {
      alert('Please select a receipt image first.');
      return;
    }
    try {
      setUploadingReceipt(true);
      const token = localStorage.getItem('token');
      const res = await shipmentService.uploadPaymentReceipt(fullDetails.shipmentId, receiptPreview, token);
      const updated = res?.shipment;
      if (updated) {
        setFullDetails(prev => prev ? ({
          ...prev,
          paymentMethod: updated.paymentMethod || prev.paymentMethod,
          paymentStatus: updated.paymentStatus || prev.paymentStatus,
          paymentReceiptData: updated.paymentReceiptData || prev.paymentReceiptData,
          paymentReceiptUploadedAt: updated.paymentReceiptUploadedAt || prev.paymentReceiptUploadedAt
        }) : prev);
      }
      alert('Receipt uploaded. Awaiting confirmation.');
    } catch (err) {
      alert(err?.response?.data?.msg || err?.message || 'Failed to upload receipt');
    } finally {
      setUploadingReceipt(false);
    }
  };

  const statusInfo = trackingData ? getStatusInfo(trackingData.status) : null;
  const normalizeStage = (raw) => {
    const s = String(raw || '').toLowerCase();
    if (s.includes('delivered')) return 'Delivered';
    if (s.includes('out for')) return 'Out for Delivery';
    if (s.includes('transit')) return 'In Transit';
    return 'Origin';
  };
  const activeStage = trackingData ? normalizeStage(trackingData.status) : 'Origin';
  const getTimestampFor = (stage) => {
    if (!trackingData?.timeline?.length) return null;
    const match = trackingData.timeline.find(e => normalizeStage(e.status) === stage);
    if (!match) return null;
    const ts = [match.date, match.time].filter(Boolean).join(' • ');
    return ts || null;
  };
  const stagesToShow = (() => {
    if (activeStage === 'Cancelled') return [{ name: 'Cancelled', type: 'error', icon: faTimesCircle }];
    if (activeStage === 'Origin') return [{ name: 'Origin', type: 'active', icon: faWarehouse }];
    if (activeStage === 'In Transit')
      return [
        { name: 'Origin', type: 'completed', icon: faWarehouse },
        { name: 'In Transit', type: 'active', icon: faTruck }
      ];
    if (activeStage === 'Out for Delivery')
      return [
        { name: 'In Transit', type: 'completed', icon: faTruck },
        { name: 'Out for Delivery', type: 'active', icon: faShippingFast }
      ];
    return [
      { name: 'Out for Delivery', type: 'completed', icon: faShippingFast },
      { name: 'Delivered', type: 'active', icon: faCheckCircle }
    ];
  })();
  const nextStepText = (() => {
    if (activeStage === 'Cancelled') return 'This shipment has been cancelled by the administrator.';
    if (activeStage === 'Origin') return 'Package will be picked up by courier within 24 hours';
    if (activeStage === 'In Transit') return 'Package will arrive at local delivery hub';
    if (activeStage === 'Out for Delivery') return 'Courier will attempt delivery today';
    if (activeStage === 'Delivered') return 'Shipment completed';
    return '';
  })();
  const getStepNumber = () => {
    if (activeStage === 'Cancelled') return 0;
    if (activeStage === 'Origin') return 1;
    if (activeStage === 'In Transit') return 2;
    if (activeStage === 'Out for Delivery') return 3;
    return 4;
  };
  const getStatusDescription = () => {
    if (activeStage === 'Cancelled') return 'Shipment cancelled.';
    if (activeStage === 'Origin') return statusConfig.origin;
    if (activeStage === 'In Transit') return statusConfig.inTransit;
    if (activeStage === 'Out for Delivery') return statusConfig.outForDelivery;
    const deliveredTs = getTimestampFor('Delivered');
    const baseMsg = statusConfig.delivered.replace(/\.$/, ''); // Remove trailing dot if present for cleaner concatenation
    return deliveredTs ? `${baseMsg} on ${deliveredTs}.` : statusConfig.delivered;
  };
  const getFriendlyLocation = () => {
    const last = trackingData?.timeline?.length ? trackingData.timeline[trackingData.timeline.length - 1] : null;
    const raw = last?.location || '';
    const bad = /(payment|agent|unknown|none|n\/a|processing|status)/i.test(raw) || raw.trim().length < 3;
    if (raw && !bad && raw !== '—') return raw;
    if (activeStage === 'Origin') return 'Origin Facility';
    if (activeStage === 'In Transit') return 'In Transit to Destination';
    if (activeStage === 'Out for Delivery') return 'Local Delivery Hub';
    if (activeStage === 'Delivered') return 'Delivered to Recipient';
    return `Status: ${trackingData?.status || '—'}`;
  };
  const lastUpdatedRelative = () => {
    const last = trackingData?.timeline?.length ? trackingData.timeline[trackingData.timeline.length - 1] : null;
    if (!last) return '—';
    if (last.ms) {
      const diffMs = Date.now() - last.ms;
      const mins = Math.floor(diffMs / 60000);
      if (mins < 1) return 'Just now';
      if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
      const hours = Math.floor(mins / 60);
      if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
      const days = Math.floor(hours / 24);
      return `${days} day${days === 1 ? '' : 's'} ago`;
    }
    return `${last.date} • ${last.time}`;
  };
  const handleRefresh = async () => {
    if (!trackingNumber.trim()) return;
    try {
      setRefreshStatus('loading');
      const resp = await shipmentService.track(trackingNumber.trim());
      const s = resp.shipment || {};
      const timeline = Array.isArray(s.statusHistory) ? s.statusHistory.map((h) => {
        let loc = h.note || h.status || 'Processing';
        const agentInfo = [h.updatedByName, h.updatedByEmail].filter(Boolean).join(' • ');
        if (agentInfo) {
          loc = loc ? `${loc} — ${agentInfo}` : agentInfo;
        }

        return {
          date: h.updatedAt ? new Date(h.updatedAt).toISOString().split('T')[0] : '',
          time: h.updatedAt ? new Date(h.updatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '',
          ms: h.updatedAt ? new Date(h.updatedAt).getTime() : null,
          location: loc,
          status: h.status || 'Update',
          updatedByName: h.updatedByName || '',
          updatedByEmail: h.updatedByEmail || '',
          updatedBy: h.updatedBy || '',
          completed: true
        };
      }) : [];
      setTrackingData((prev) => ({
        id: formatTrackingCode(s.trackingCode || trackingNumber),
        status: s.status || prev?.status || 'Pending',
        estimatedDelivery: prev?.estimatedDelivery || '—',
        origin: s.pickupLocation || prev?.origin || '—',
        destination: s.receiverName ? `${s.receiverName}` : (prev?.destination || '—'),
        serviceType: s.serviceType || prev?.serviceType || 'interstate',
        subtype: s.internationalSubtype || prev?.subtype,
        sender: {
          name: s.senderName || prev?.sender?.name || '—',
          phone: s.senderNumber || prev?.sender?.phone || '—',
          email: s.email || prev?.sender?.email || '—',
          address: s.senderAddress || prev?.sender?.address || '—'
        },
        receiver: {
          name: s.receiverName || prev?.receiver?.name || '—',
          phone: s.receiverNumber || prev?.receiver?.phone || '—',
          email: prev?.receiver?.email || '—',
          address: s.receiverAddress || prev?.receiver?.address || '—'
        },
        package: {
          weight: s.weight ? `${s.weight} kg` : (prev?.package?.weight || '—'),
          dimensions: prev?.package?.dimensions || '—',
          description: s.packageType || prev?.package?.description || '—',
          items: s.numberOfItems || prev?.package?.items || '—',
          height: s.height || prev?.package?.height,
          width: s.width || prev?.package?.width,
          contentList: s.contentList || prev?.package?.contentList
        },
        timeline
      }));
      setAnimateProgress(false);
      setTimeout(() => setAnimateProgress(true), 100);
      setRefreshStatus('success');
      setTimeout(() => setRefreshStatus('idle'), 1200);
    } catch (e) {
      setRefreshStatus('idle');
    }
  };

  const goBack = () => {
    const role = localStorage.getItem('role');
    if (role === 'ecommerce') {
      navigate('/dashboard/ecommerce/shipment-history');
    } else {
      navigate('/dashboard/user/shipments');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 pb-20 md:pb-6">
      {/* Mobile-Optimized Header */}
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={goBack}
              className="w-10 h-10 flex items-center justify-center rounded-xl bg-gray-100 text-gray-700 hover:bg-gray-200"
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </button>
            <h1 className="text-lg font-bold text-gray-900">Track Package</h1>
            <div className="w-10"></div>
          </div>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Search Box */}
        <div className="bg-white rounded-2xl shadow-md p-5 mb-6">
          <label className="block text-sm font-semibold text-gray-700 mb-3">
            Enter Tracking Number
          </label>
          <div className="flex gap-3">
            <div className="relative flex-1">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <FontAwesomeIcon icon={faSearch} className="text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="e.g., FX1xxxxx"
                className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                value={trackingNumber}
                onChange={(e) => setTrackingNumber(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleTrackShipment()}
              />
            </div>
            <button
              onClick={handleTrackShipment}
              disabled={loading}
              className="px-6 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-all font-medium"
            >
              {loading ? (
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : (
                <FontAwesomeIcon icon={faSearch} />
              )}
            </button>
          </div>
          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm flex items-start">
              <FontAwesomeIcon icon={faTimesCircle} className="mr-2 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
        </div>

        {/* Tracking Results */}
        {trackingData && (
          <div className="space-y-5 animate-fade-in">
            {/* Animated Progress Journey */}
            <div className="bg-white rounded-2xl shadow-md p-6 overflow-hidden">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-6">
                <h2 className="text-lg font-bold text-gray-900">Shipment Status</h2>
                <span className={`px-3 py-1.5 text-xs font-semibold rounded-full ${statusInfo.bg} ${statusInfo.text}`}>
                  {trackingData.status}
                </span>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between text-xs text-gray-600 mb-4 gap-1">
                <span>Step {getStepNumber()} of 4</span>
                <span>{statusInfo.progress}%</span>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between text-xs text-gray-600 mb-2 gap-2">
                <span>Last updated: {lastUpdatedRelative()}</span>
                <button
                  onClick={handleRefresh}
                  className="inline-flex items-center px-2 py-1 rounded-md bg-gray-100 hover:bg-gray-200 transition self-start sm:self-auto"
                >
                  {refreshStatus === 'loading' ? (
                    <svg className="animate-spin h-3 w-3 text-gray-700 mr-1.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : refreshStatus === 'success' ? (
                    <span className="mr-1.5">✅</span>
                  ) : (
                    <span className="mr-1.5">🔄</span>
                  )}
                  <span className="text-gray-800">Refresh Status</span>
                </button>
              </div>

              {/* Visual Journey Map */}
              <div className="relative mb-8">
                <div className="absolute top-8 left-0 right-0 h-1 bg-gray-200 rounded-full hidden sm:block"></div>
                <div
                  className={`absolute top-8 left-0 h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-green-500 rounded-full transition-all duration-1000 ease-out hidden sm:block ${animateProgress ? 'opacity-100' : 'opacity-0'
                    }`}
                  style={{
                    width: animateProgress ? `${statusInfo.progress}%` : '0%',
                    transition: 'width 1.5s ease-out'
                  }}
                ></div>
                <div className="relative flex items-start pt-2">
                  <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6 mx-auto">
                    {stagesToShow.map((stg, idx) => {
                      const isActive = stg.type === 'active';
                      const isCompleted = stg.type === 'completed';
                      const size = isActive ? 'w-16 h-16 sm:w-20 sm:h-20' : 'w-14 h-14 sm:w-16 sm:h-16';
                      const bg = isActive ? 'bg-blue-500' : isCompleted ? 'bg-green-500' : 'bg-gray-200';
                      const pulse = isActive ? 'animate-pulse' : '';
                      const ts = getTimestampFor(stg.name);
                      const iconColor = isActive || isCompleted ? 'text-white' : 'text-gray-400';
                      return (
                        <React.Fragment key={stg.name}>
                          <div className="flex flex-col items-center">
                            <div className={`${size} rounded-full flex items-center justify-center mb-2 ${bg} ${pulse}`}>
                              <FontAwesomeIcon icon={stg.icon} className={`text-xl sm:text-2xl ${iconColor}`} />
                              {isCompleted && (
                                <span className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-green-600 flex items-center justify-center">
                                  <FontAwesomeIcon icon={faCheckCircle} className="text-white text-xs" />
                                </span>
                              )}
                            </div>
                            <p className={`text-[11px] sm:text-xs font-semibold ${isActive ? 'text-gray-900' : 'text-gray-700'} text-center`}>{stg.name}</p>
                            {isCompleted && ts && (
                              <p className="text-[10px] text-gray-500 mt-0.5">{ts}</p>
                            )}
                            {isActive && stg.name === 'Delivered' && (
                              <p className="text-[10px] text-gray-500 mt-0.5">{getTimestampFor('Delivered') || '—'}</p>
                            )}
                          </div>
                          {idx < stagesToShow.length - 1 && (
                            <div className="hidden sm:flex items-center">
                              <div className="w-12 h-0.5 bg-gray-300 rounded-full mr-2"></div>
                              <div className="text-gray-400">
                                →
                              </div>
                              <div className="w-12 h-0.5 bg-gray-300 rounded-full ml-2"></div>
                            </div>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Quick Info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t border-gray-100">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Tracking ID</p>
                  <p className="font-semibold text-sm text-gray-900">{trackingData.id}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Est. Delivery</p>
                  <div className="font-semibold text-sm text-gray-900">
                    {(() => {
                      if (activeStage === 'Delivered') {
                        return getTimestampFor('Delivered')?.split(' • ')[0] || 'Delivered';
                      }

                      const type = trackingData.serviceType || inferTypeFromCode(trackingData.id);

                      if (type === 'international') return '—';

                      if (type === 'local') {
                        if (activeStage === 'In Transit' || activeStage === 'Out for Delivery') {
                          const match = trackingData.timeline?.slice().reverse().find(e => normalizeStage(e.status) === activeStage);
                          if (match && match.ms) {
                            return <CountdownTimer targetDate={match.ms + (6 * 60 * 60 * 1000)} />;
                          }
                          return 'Within 6 hours';
                        }
                        return 'Available once in transit';
                      }

                      if (activeStage === 'In Transit' || activeStage === 'Out for Delivery') {
                        return '3-4 working days';
                      }
                      return '3-4 working days';
                    })()}
                  </div>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-gray-100">
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Current Status Details</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Current Status</p>
                    <p className="font-semibold text-sm text-gray-900">{trackingData.status}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Current Details</p>
                    <p className="font-semibold text-sm text-gray-900">{getFriendlyLocation()}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Last Updated</p>
                    <p className="font-semibold text-sm text-gray-900">{(trackingData.timeline && trackingData.timeline.length) ? `${trackingData.timeline[trackingData.timeline.length - 1].date} • ${trackingData.timeline[trackingData.timeline.length - 1].time}` : '—'}</p>
                  </div>
                </div>
                <div className="mt-3 p-3 bg-blue-50 rounded-xl text-xs text-blue-700 inline-flex items-center">
                  <span className="mr-2">🔔</span>
                  <span><strong>Next:</strong> {nextStepText}</span>
                </div>
              </div>
              <div className="mt-2 p-3 bg-gray-50 rounded-xl text-sm text-gray-800">
                {getStatusDescription()}
              </div>

              {fullDetails && (
                <div className="mt-4 pt-4 border-t border-gray-100 space-y-3">
                  {(() => {
                    const confirmed = isPaymentConfirmed(fullDetails.paymentStatus);
                    const ref = String(fullDetails.paymentReferenceCode || '').trim();
                    const refAt = fullDetails.paymentReferenceSavedAt ? new Date(fullDetails.paymentReferenceSavedAt) : null;
                    const confAt = fullDetails.paymentConfirmedAt ? new Date(fullDetails.paymentConfirmedAt) : null;
                    const method = paymentMethodChoice || (fullDetails.paymentMethod || '');
                    const showBankDetails = method === 'bank_transfer';
                    const showCashDetails = method === 'cash';
                    return (
                      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                        <button
                          onClick={() => setShowPaymentDetails(!showPaymentDetails)}
                          className="w-full px-4 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-center">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 ${confirmed ? 'bg-green-100' : 'bg-yellow-100'}`}>
                              <FontAwesomeIcon icon={faCreditCard} className={confirmed ? 'text-green-600' : 'text-yellow-600'} />
                            </div>
                            <div className="text-left">
                              <p className="text-sm font-bold text-gray-900">Payment Information</p>
                              <p className={`text-[10px] font-bold uppercase tracking-wider ${confirmed ? 'text-green-600' : 'text-yellow-600'}`}>
                                {confirmed ? 'Payment Confirmed' : 'Pending Payment'}
                              </p>
                            </div>
                          </div>
                          <FontAwesomeIcon
                            icon={showPaymentDetails ? faChevronUp : faChevronDown}
                            className="text-gray-400"
                          />
                        </button>

                        {showPaymentDetails && (
                          <div className="px-4 pb-4 space-y-3 animate-slide-down">
                            {ref && (
                              <div className="mb-3">
                                <p className="text-xs text-gray-500 font-bold uppercase">Payment Reference Code</p>
                                <p className="font-mono text-sm font-bold text-gray-900 break-all">{ref}</p>
                                {refAt && (
                                  <p className="text-xs text-gray-600 mt-1">
                                    Confirmed on {refAt.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                                  </p>
                                )}
                              </div>
                            )}

                            {confirmed && confAt && (
                              <p className="text-xs text-gray-600">
                                Confirmed on {confAt.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>

            {/* Collapsible Sender Details */}
            <div className="bg-white rounded-2xl shadow-md overflow-hidden">
              <button
                onClick={() => setShowSenderDetails(!showSenderDetails)}
                className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                    <FontAwesomeIcon icon={faUser} className="text-blue-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900">Sender Information</h3>
                </div>
                <FontAwesomeIcon
                  icon={showSenderDetails ? faChevronUp : faChevronDown}
                  className="text-gray-400"
                />
              </button>
              {showSenderDetails && (
                <div className="px-5 pb-5 space-y-3 animate-slide-down">
                  <div className="flex items-start">
                    <FontAwesomeIcon icon={faUser} className="text-gray-400 mt-1 mr-3 w-4" />
                    <div className="flex-1">
                      <p className="text-xs text-gray-500">Name</p>
                      <p className="text-sm font-medium text-gray-900">{trackingData.sender.name}</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <FontAwesomeIcon icon={faPhone} className="text-gray-400 mt-1 mr-3 w-4" />
                    <div className="flex-1">
                      <p className="text-xs text-gray-500">Phone</p>
                      <p className="text-sm font-medium text-gray-900">{trackingData.sender.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <FontAwesomeIcon icon={faMapMarkerAlt} className="text-gray-400 mt-1 mr-3 w-4" />
                    <div className="flex-1">
                      <p className="text-xs text-gray-500">Address</p>
                      <p className="text-sm font-medium text-gray-900">{trackingData.sender.address}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Collapsible Receiver Details */}
            {trackingData.status === 'Delivered' && (
              <div className="bg-white rounded-2xl shadow-md overflow-hidden">
                <button
                  onClick={() => setShowReceiverDetails(!showReceiverDetails)}
                  className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center">
                    <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center mr-3">
                      <FontAwesomeIcon icon={faUser} className="text-green-600" />
                    </div>
                    <h3 className="font-semibold text-gray-900">Receiver Information</h3>
                  </div>
                  <FontAwesomeIcon
                    icon={showReceiverDetails ? faChevronUp : faChevronDown}
                    className="text-gray-400"
                  />
                </button>
                {showReceiverDetails && (
                  <div className="px-5 pb-5 space-y-3 animate-slide-down">
                    <div className="flex items-start">
                      <FontAwesomeIcon icon={faUser} className="text-gray-400 mt-1 mr-3 w-4" />
                      <div className="flex-1">
                        <p className="text-xs text-gray-500">Name</p>
                        <p className="text-sm font-medium text-gray-900">{trackingData.receiver.name}</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <FontAwesomeIcon icon={faPhone} className="text-gray-400 mt-1 mr-3 w-4" />
                      <div className="flex-1">
                        <p className="text-xs text-gray-500">Phone</p>
                        <p className="text-sm font-medium text-gray-900">{trackingData.receiver.phone}</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <FontAwesomeIcon icon={faMapMarkerAlt} className="text-gray-400 mt-1 mr-3 w-4" />
                      <div className="flex-1">
                        <p className="text-xs text-gray-500">Address</p>
                        <p className="text-sm font-medium text-gray-900">{trackingData.receiver.address}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Collapsible Package Details */}
            <div className="bg-white rounded-2xl shadow-md overflow-hidden">
              <button
                onClick={() => setShowPackageDetails(!showPackageDetails)}
                className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center">
                  <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center mr-3">
                    <FontAwesomeIcon icon={faBox} className="text-purple-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900">Package Details</h3>
                </div>
                <FontAwesomeIcon
                  icon={showPackageDetails ? faChevronUp : faChevronDown}
                  className="text-gray-400"
                />
              </button>
              {showPackageDetails && (
                <div className="px-5 pb-5">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Weight</p>
                      <p className="text-sm font-medium text-gray-900">{trackingData.package.weight}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Items</p>
                      <p className="text-sm font-medium text-gray-900">{trackingData.package.items}</p>
                    </div>
                    {(trackingData.package.height || trackingData.package.width) && (
                      <>
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Height</p>
                          <p className="text-sm font-medium text-gray-900">{trackingData.package.height} cm</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Width</p>
                          <p className="text-sm font-medium text-gray-900">{trackingData.package.width} cm</p>
                        </div>
                      </>
                    )}
                    <div className="col-span-2">
                      <p className="text-xs text-gray-500 mb-1">Type</p>
                      <p className="text-sm font-medium text-gray-900">{trackingData.package.description}</p>
                    </div>
                    {trackingData.package.contentList && (
                      <div className="col-span-2">
                        <p className="text-xs text-gray-500 mb-1">Content List</p>
                        <p className="text-sm font-medium text-gray-900 whitespace-pre-wrap">{trackingData.package.contentList}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Timeline */}
            {trackingData.timeline.length > 0 && (
              <div className="bg-white rounded-2xl shadow-md p-5">
                <h3 className="font-semibold text-gray-900 mb-5 flex items-center">
                  <FontAwesomeIcon icon={faClock} className="mr-2 text-gray-600" />
                  Tracking History
                </h3>
                <div className="relative">
                  <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>
                  <div className="space-y-5">
                    {trackingData.timeline.map((event, index) => (
                      <div key={index} className="relative flex items-start animate-slide-in" style={{ animationDelay: `${index * 100}ms` }}>
                        <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center z-10 ${event.completed ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-600'
                          }`}>
                          <FontAwesomeIcon icon={getStatusInfo(event.status).icon} className="text-xs" />
                        </div>
                        <div className="ml-4 min-w-0 flex-1">
                          <p className="text-sm font-semibold text-gray-900">{event.status}</p>
                          <div className="flex items-center text-xs text-gray-500 mt-1">
                            <FontAwesomeIcon icon={faCalendarAlt} className="mr-1" />
                            {event.date} • {event.time}
                          </div>
                          <p className="text-xs text-gray-600 mt-1">{event.location}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      <style jsx>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slide-down {
          from {
            opacity: 0;
            max-height: 0;
          }
          to {
            opacity: 1;
            max-height: 500px;
          }
        }

        @keyframes slide-in {
          from {
            opacity: 0;
            transform: translateX(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        .animate-fade-in {
          animation: fade-in 0.5s ease-out;
        }

        .animate-slide-down {
          animation: slide-down 0.3s ease-out;
        }

        .animate-slide-in {
          animation: slide-in 0.5s ease-out;
        }
      `}</style>
    </div>
  );
};

export default Tracking;
